/**
 * useMemberReportIssue — the state and actions of MemberReportIssueDialog, shared by the legacy render and MemberReportIssueDialogNext.
 */
import { useEffect, useMemo, useState } from 'react';
import { saveAs } from 'file-saver';
import api from '../../../services/api.js';
import {
  DEFAULT_COL_KEYS,
  EXPORT_FIELD_OPTIONS,
} from './memberReportMergeFields.js';
import { printMemberList, printMemberBreakdown, printMemberCards } from './memberPrint.js';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';

export const CARD_PRINT_WARN = 200;
export const ZIP_TIMEOUT_MS = 10 * 60 * 1000;

export function relatedDefaults(reportType) {
  const card = reportType === 'card';
  return { registrations: card, grades: card };
}

export async function blobErrorMessage(err, fallback) {
  if (err?.response?.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      const parsed = JSON.parse(text);
      return parsed.error || fallback;
    } catch {
      return fallback;
    }
  }
  return err?.response?.data?.error || err?.message || fallback;
}

export function filenameFromDisposition(disposition, fallback) {
  const match = (disposition || '').match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)/i)
    || (disposition || '').match(/filename=\"?([^\"]+)\"?/i);
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function useMemberReportIssue(props) {
  const {
    open,
    onClose,
    filteredMemberIds = [],
    filterSummary = [],
    filteredCount = 0,
    selectedIds = [],
    periods = [],
  } = props;
  const [step, setStep] = useState('picker');
  const [reportType, setReportType] = useState('list');
  const [source, setSource] = useState('filters');
  const [selectedCols, setSelectedCols] = useState(() => new Set(DEFAULT_COL_KEYS));
  const [includeRegistrations, setIncludeRegistrations] = useState(false);
  const [includeGrades, setIncludeGrades] = useState(false);
  const [contentMode, setContentMode] = useState('members');
  const [kolelSlot, setKolelSlot] = useState('primary');
  const [groupBy, setGroupBy] = useState('city');
  const [includeMembers, setIncludeMembers] = useState(true);
  const [examPeriod, setExamPeriod] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [customColumns, setCustomColumns] = useState([]);
  const [customColumnPlacement, setCustomColumnPlacement] = useState('end');
  const [customColumnInput, setCustomColumnInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isByKolel = reportType === 'byKolel';
  const needsRelatedFilters = isByKolel
    ? (contentMode === 'registrations' || contentMode === 'both')
    : (includeRegistrations || includeGrades);
  const showCustomColumns = reportType !== 'card'
    && !(isByKolel && contentMode === 'registrations');

  const columnOptions = EXPORT_FIELD_OPTIONS;
  const typeTemplates = useMemo(
    () => templates.filter((t) => t.targetType === reportType),
    [templates, reportType]
  );

  const pickerOptions = useMemo(() => [
    {
      key: 'list',
      title: 'רשימת חברים',
      description: 'עמוד מכתב (עם רקע אם הוגדר) ואז טבלת חברים להדפסה או Excel',
      ckIcon: 'list',
    },
    {
      key: 'breakdown',
      title: 'פילוח',
      description: 'עמוד מכתב ואז קיבוץ לפי עיר, כולל, סטטוס ועוד',
      ckIcon: 'chart',
    },
    {
      key: 'card',
      title: 'כרטיס נבחן',
      description: 'כרטיס A4 לכל חבר — תבנית ורקע מכתב על כל כרטיס',
      ckIcon: 'card',
    },
    {
      key: 'byKolel',
      title: 'רשימות לפי כולל',
      description: 'קובץ Excel/PDF נפרד לכל כולל — באריזת ZIP (ללא רקע מכתב)',
      ckIcon: 'folder',
    },
  ], []);

  useEffect(() => {
    if (!open) return;
    setStep('picker');
    setReportType('list');
    setSource(selectedIds.length ? 'selected' : 'filters');
    setError('');
    setBusy(false);
    setSelectedCols(new Set(DEFAULT_COL_KEYS));
    setTemplateId('');
    setTemplates([]);
    setExamPeriod(null);
    setDateFrom('');
    setDateTo('');
    setContentMode('members');
    setKolelSlot('primary');
    setCustomColumns([]);
    setCustomColumnPlacement('end');
    setCustomColumnInput('');
  }, [open, selectedIds.length]);

  const loadTemplates = async (type) => {
    if (type === 'byKolel') {
      setTemplates([]);
      setTemplateId('');
      return;
    }
    try {
      const { data } = await api.get('/member-reports/templates');
      const list = data.data || [];
      setTemplates(list);
      const match = list.find((t) => t.targetType === type);
      setTemplateId(match ? String(match._id) : '');
    } catch {
      setTemplates([]);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/member-reports/history');
      setHistory(data.data || []);
      setShowHistory(true);
    } catch {
      setHistory([]);
      setShowHistory(true);
    }
  };

  const pickType = (key) => {
    setReportType(key);
    const rel = relatedDefaults(key);
    setIncludeRegistrations(rel.registrations);
    setIncludeGrades(rel.grades);
    if (key === 'byKolel') {
      setContentMode('members');
      setKolelSlot('primary');
      setSource('all');
    }
    setStep('options');
    setError('');
    loadTemplates(key);
  };

  const toggleCol = (key) => {
    setSelectedCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const addCustomColumn = () => {
    const label = customColumnInput.trim().slice(0, 40);
    if (!label) return;
    setCustomColumns((prev) => {
      if (prev.includes(label) || prev.length >= 10) return prev;
      return [...prev, label];
    });
    setCustomColumnInput('');
  };

  const removeCustomColumn = (label) => {
    setCustomColumns((prev) => prev.filter((c) => c !== label));
  };

  const resolveMemberIds = () => {
    if (source === 'selected') return selectedIds;
    if (source === 'filters') return filteredMemberIds;
    return []; // all — server loads all
  };

  const validatePopulation = () => {
    const needsColumns = !(isByKolel && contentMode === 'registrations');
    if (needsColumns && !selectedCols.size) {
      setError('יש לבחור לפחות עמודה אחת');
      return false;
    }
    if (source === 'selected' && !selectedIds.length) {
      setError('אין שורות מסומנות');
      return false;
    }
    if (source === 'filters' && !filteredMemberIds.length) {
      setError('אין חברים לפי הסינון הנוכחי');
      return false;
    }
    return true;
  };

  const buildBody = (format = 'json') => {
    const cols = columnOptions.filter((c) => selectedCols.has(c.key)).map((c) => c.key);
    const memberIds = resolveMemberIds();
    const related = isByKolel
      ? {
        registrations: contentMode === 'registrations' || contentMode === 'both',
        grades: false,
      }
      : { registrations: includeRegistrations, grades: includeGrades };
    return {
      source,
      memberIds: source === 'all' ? [] : memberIds,
      reportType,
      groupBy,
      includeMembers,
      columns: cols,
      related,
      contentMode: isByKolel ? contentMode : undefined,
      kolelSlot: isByKolel ? kolelSlot : undefined,
      splitBy: isByKolel ? 'kolel' : undefined,
      examPeriodId: examPeriod?._id || null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
      filterSummary,
      templateId: isByKolel ? null : (templateId || null),
      format,
      customColumns,
      customColumnPlacement,
    };
  };

  const runPrint = (report) => {
    const meta = report.meta || {};
    const title = meta.reportTypeLabel || 'דוח חברים';
    const cols = report.columns || [];
    const extras = {
      letterHtml: meta.letterHtml || '',
      blankUrl: meta.blankUrl || '',
      letterInsets: meta.letterInsets || undefined,
      periodName: meta.periodName || '',
    };
    if (meta.reportType === 'breakdown') {
      printMemberBreakdown({
        title,
        groupByLabel: meta.groupByLabel,
        filterSummary: meta.filterSummary || filterSummary,
        columns: cols,
        groups: report.groups || [],
        ...extras,
      });
      return;
    }
    if (meta.reportType === 'card') {
      printMemberCards({
        title,
        filterSummary: meta.filterSummary || filterSummary,
        rows: report.members || [],
        includeRegistrations: !!meta.includeRegistrations,
        includeGrades: !!meta.includeGrades,
        ...extras,
      });
      return;
    }
    printMemberList({
      title,
      filterSummary: meta.filterSummary || filterSummary,
      columns: cols,
      rows: report.members || [],
      includeRegistrations: !!meta.includeRegistrations,
      ...extras,
    });
  };

  const handlePrint = async () => {
    if (!validatePopulation()) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/member-reports', buildBody('json'));
      const report = data.data;
      if (!report?.meta?.memberCount) {
        setError('אין רשומות להנפקה לפי הבחירה הנוכחית');
        return;
      }
      if (reportType === 'card' && report.meta.memberCount > CARD_PRINT_WARN) {
        const ok = (await ckConfirm({ title: 'הדפסת כרטיסי נבחן', message: `יופקו ${report.meta.memberCount.toLocaleString('he-IL')} כרטיסי נבחן. להמשיך בהדפסה?`, confirmLabel: 'המשך להדפסה', danger: false }));
        if (!ok) return;
      }
      runPrint(report);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'שגיאה בהנפקת הדוח');
    } finally {
      setBusy(false);
    }
  };

  const handleExcel = async () => {
    if (!validatePopulation()) return;
    setBusy(true);
    setError('');
    try {
      const response = await api.post('/member-reports', buildBody('xlsx'), { responseType: 'blob' });
      const filename = filenameFromDisposition(
        response.headers['content-disposition'],
        `member-report-${reportType}.xlsx`
      );
      saveAs(new Blob([response.data]), filename);
      onClose?.();
    } catch (err) {
      setError(await blobErrorMessage(err, 'שגיאה בייצוא Excel'));
    } finally {
      setBusy(false);
    }
  };

  const handleByKolelZip = async (format) => {
    if (!validatePopulation()) return;
    setBusy(true);
    setError('');
    try {
      const response = await api.post(
        '/member-reports',
        buildBody(format),
        { responseType: 'blob', timeout: ZIP_TIMEOUT_MS }
      );
      const day = new Date().toISOString().slice(0, 10);
      const filename = filenameFromDisposition(
        response.headers['content-disposition'],
        `דוחות-לפי-כולל-${day}.zip`
      );
      saveAs(new Blob([response.data], { type: 'application/zip' }), filename);
      onClose?.();
    } catch (err) {
      setError(await blobErrorMessage(
        err,
        format === 'pdf-zip' ? 'שגיאה בייצוא PDF לפי כולל' : 'שגיאה בייצוא Excel לפי כולל'
      ));
    } finally {
      setBusy(false);
    }
  };

  const typeTitle = pickerOptions.find((o) => o.key === reportType)?.title || 'הנפקת דוח';

  return {
    step,
    setStep,
    reportType,
    setReportType,
    source,
    setSource,
    selectedCols,
    setSelectedCols,
    includeRegistrations,
    setIncludeRegistrations,
    includeGrades,
    setIncludeGrades,
    contentMode,
    setContentMode,
    kolelSlot,
    setKolelSlot,
    groupBy,
    setGroupBy,
    includeMembers,
    setIncludeMembers,
    examPeriod,
    setExamPeriod,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    templates,
    setTemplates,
    templateId,
    setTemplateId,
    editorOpen,
    setEditorOpen,
    history,
    setHistory,
    showHistory,
    setShowHistory,
    customColumns,
    setCustomColumns,
    customColumnPlacement,
    setCustomColumnPlacement,
    customColumnInput,
    setCustomColumnInput,
    busy,
    setBusy,
    error,
    setError,
    isByKolel,
    needsRelatedFilters,
    showCustomColumns,
    columnOptions,
    typeTemplates,
    pickerOptions,
    loadTemplates,
    loadHistory,
    pickType,
    toggleCol,
    addCustomColumn,
    removeCustomColumn,
    resolveMemberIds,
    validatePopulation,
    buildBody,
    runPrint,
    handlePrint,
    handleExcel,
    handleByKolelZip,
    typeTitle,
    open,
    onClose,
    filteredMemberIds,
    filterSummary,
    filteredCount,
    selectedIds,
    periods,
  };
}
